import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {Record<string, string> | null} Variant id -> warehouse "Lagerstatus" text. */
  #lagerstatusMap = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#setupBundleSelector();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  #setupBundleSelector() {
    const bundleControls = this.querySelectorAll('[data-bundle-package]');
    if (!bundleControls.length) return;

    const { signal } = this.#abortController;
    bundleControls.forEach((input) => {
      input.addEventListener('change', this.#syncBundleProperty, { signal });
    });

    this.#setupBundleMattressModal();
    this.#syncBundleProperty();
  }

  /** Wires the "swap mattress" modal: openers, alternative cards, and close controls. */
  #setupBundleMattressModal() {
    const modal = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-bundle-mattress-modal]'));
    if (!modal) return;

    const { signal } = this.#abortController;

    this.querySelectorAll('[data-bundle-mattress-open]').forEach((trigger) => {
      trigger.addEventListener('click', this.#openMattressModal, { signal });
      trigger.addEventListener('keydown', this.#onMattressTriggerKeydown, { signal });
    });

    modal.querySelectorAll('[data-bundle-mattress-choice]').forEach((card) => {
      card.addEventListener('click', this.#chooseMattress, { signal });
    });

    modal.querySelectorAll('[data-bundle-mattress-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', () => modal.close(), { signal });
    });

    // Close when clicking the backdrop (clicks on the dialog element itself, outside its inner panel).
    modal.addEventListener(
      'click',
      (event) => {
        if (event.target === modal) modal.close();
      },
      { signal }
    );
  }

  /** @param {Event} event */
  #openMattressModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const modal = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-bundle-mattress-modal]'));
    modal?.showModal?.();
  };

  /** @param {KeyboardEvent} event */
  #onMattressTriggerKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      this.#openMattressModal(event);
    }
  };

  /**
   * Swaps the bundled mattress to the chosen alternative, updating the paired variant id and all
   * price/label displays from values pre-formatted server-side (avoids client-side money formatting).
   * @param {Event} event
   */
  #chooseMattress = (event) => {
    const card = /** @type {HTMLElement} */ (event.currentTarget);
    const data = card.dataset;
    const root = this.querySelector('[data-bundle-selector]');
    if (!root) return;

    const featuredInput = /** @type {HTMLInputElement | null} */ (
      root.querySelector('.bundle-option--featured [data-bundle-package]')
    );
    if (featuredInput && data.variantId) {
      // Preserve the bundle quantity (e.g. bunk beds add the chosen mattress twice).
      const variantQty = Math.max(1, parseInt(data.variantQty || '1', 10) || 1);
      featuredInput.dataset.bundleVariantIds = variantQty > 1 ? `${data.variantId}:${variantQty}` : data.variantId;
    }

    /** @param {string} selector @param {string | undefined} value */
    const setText = (selector, value) => {
      if (value === undefined) return;
      root.querySelectorAll(selector).forEach((el) => {
        el.textContent = value;
      });
    };

    /** @param {string} selector @param {string | undefined} src */
    const setImage = (selector, src) => {
      if (!src) return;
      root.querySelectorAll(selector).forEach((el) => {
        const img = el instanceof HTMLImageElement ? el : el.querySelector('img');
        if (img) {
          img.src = src;
          img.removeAttribute('srcset');
        }
      });
    };

    setText('[data-bundle-addon-name]', data.addonName);
    setText('[data-bundle-addon-price]', data.addonPrice);
    setText('[data-bundle-primary-price]', data.totalPrice);
    if (data.displaySavings !== undefined) {
      setText('[data-bundle-primary-savings-text]', `Pakketilbud: Spar ${data.displaySavings}`);
      setText('[data-bundle-primary-saving-value]', data.displaySavings);
      setText('[data-bundle-primary-ribbon-saving]', data.displaySavings);
    }
    setText('[data-bundle-primary-compare]', data.displayCompare);
    if (data.percent !== undefined) setText('[data-bundle-primary-percent]', `${data.percent}% RABATT`);
    setText('[data-bundle-addon-tooltip-desc]', data.desc);

    setImage('[data-bundle-addon-thumb]', data.thumb);
    setImage('[data-bundle-addon-summary-thumb]', data.thumb);
    setImage('[data-bundle-addon-tooltip-image]', data.image);

    root.querySelectorAll('[data-bundle-mattress-choice]').forEach((other) => {
      other.classList.toggle('is-selected', other === card);
    });

    // Make sure the "bed + mattress" package is active so the chosen mattress is what gets added.
    if (featuredInput && !featuredInput.checked) {
      featuredInput.checked = true;
    }
    this.#syncBundleProperty();

    const modal = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-bundle-mattress-modal]'));
    modal?.close();
  };

  #syncBundleProperty = () => {
    const selectedPackage = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bundle-package]:checked'));
    const bundlePropertyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bundle-selection-property]'));
    const bundleSummary = this.querySelector('[data-bundle-live-summary]');

    if (!selectedPackage || !bundlePropertyInput) return;

    const bundleLabel = this.#buildBundleLabel(selectedPackage);

    bundlePropertyInput.value = bundleLabel;

    if (bundleSummary) {
      bundleSummary.textContent = `${bundleLabel} legges til med ett klikk.`;
    }

    this.dispatchEvent(
      new CustomEvent('bundleSelectionChange', {
        bubbles: true,
        detail: {
          label: bundleLabel,
        },
      })
    );
  };

  /**
   * @param {HTMLInputElement} selectedPackage
   * @returns {string}
   */
  #buildBundleLabel(selectedPackage) {
    const packageLabel = selectedPackage.dataset.bundlePackageLabel || '';
    const usesMattressSizes = selectedPackage.dataset.bundleUseMattressSizes === 'true';

    if (!usesMattressSizes) return packageLabel;

    const sizeOneSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-bundle-mattress-size-one]'));
    const sizeTwoSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-bundle-mattress-size-two]'));

    const sizeOneText = sizeOneSelect?.selectedOptions?.[0]?.textContent?.split(' - ')[0]?.trim();
    const sizeTwoText = sizeTwoSelect?.selectedOptions?.[0]?.textContent?.split(' - ')[0]?.trim();

    const normalizedOne = this.#extractNormalizedSizeToken(sizeOneText);
    const normalizedTwo = this.#extractNormalizedSizeToken(sizeTwoText);

    const selectedSizes = [
      normalizedOne || sizeOneText,
      normalizedTwo || sizeTwoText,
    ]
      .filter(Boolean)
      .join(' + ');

    if (!selectedSizes) return packageLabel;

    return `${packageLabel} (${selectedSizes})`;
  }

  /**
   * Regex-style size extractor. Normalizes values like "90 x 200", "90/200", "90-200cm" to "90x200".
   * @param {string | undefined} value
   * @returns {string}
   */
  #extractNormalizedSizeToken(value) {
    if (!value) return '';

    const lowered = value.toLowerCase();
    const matched = lowered.match(/(\d{2,3})\s*[x×\/-]\s*(\d{2,3})/);
    if (matched?.[1] && matched?.[2]) {
      return `${matched[1]}x${matched[2]}`;
    }

    const compact = lowered.replace(/cm|\s|\.|-/g, '');
    const fallback = compact.match(/(\d{2,3})[x×\/-]?(\d{2,3})/);
    if (fallback?.[1] && fallback?.[2]) {
      return `${fallback[1]}x${fallback[2]}`;
    }

    return value.trim();
  }

  /** @returns {Array<{variantId: string, quantity: number}>} */
  #getSelectedBundleItems() {
    const selectedPackage = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bundle-package]:checked'));
    if (!selectedPackage) return [];

    // Map keeps per-variant quantities so a bunk bed can add the same mattress twice ("id:2").
    /** @type {Map<string, number>} */
    const variantQuantities = new Map();
    const rawVariantIds = selectedPackage.dataset.bundleVariantIds || '';
    const mainVariantId = this.refs.variantId?.value;

    /** @param {string} id @param {number} quantity */
    const addVariant = (id, quantity) => {
      const trimmedId = id.trim();
      if (!trimmedId) return;
      variantQuantities.set(trimmedId, (variantQuantities.get(trimmedId) || 0) + quantity);
    };

    // Each entry is "variantId" or "variantId:quantity" (quantity defaults to 1).
    rawVariantIds
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const [id, qty] = entry.split(':');
        addVariant(id, Math.max(1, parseInt(qty, 10) || 1));
      });

    const usesMattressSizes = selectedPackage.dataset.bundleUseMattressSizes === 'true';
    if (usesMattressSizes) {
      const sizeOneVariantId = /** @type {HTMLSelectElement | null} */ (
        this.querySelector('[data-bundle-mattress-size-one]')
      )?.value;
      const sizeTwoVariantId = /** @type {HTMLSelectElement | null} */ (
        this.querySelector('[data-bundle-mattress-size-two]')
      )?.value;

      if (sizeOneVariantId) addVariant(sizeOneVariantId, 1);
      if (sizeTwoVariantId) addVariant(sizeTwoVariantId, 1);
    }

    return Array.from(variantQuantities.entries())
      .filter(([variantId]) => variantId !== mainVariantId)
      .map(([variantId, quantity]) => ({
        variantId,
        quantity,
      }));
  }

  /**
   * The add-ons (mattress, and on the Gamer ULTIMATE beds the sovesofa) of the package the
   * customer has chosen, shaped for a /cart/add.js POST. The bed itself is deliberately left out:
   * it is the product form's own line, so whoever calls this already has it.
   *
   * Empty on "Kun seng" and on products without a bundle. Public because the Vipps express button
   * is an app block outside this component — see bundle-express-checkout.js.
   *
   * @returns {Array<{id: number, quantity: number, properties: Record<string, string>}>}
   */
  getBundleAddonItems() {
    return this.#getSelectedBundleItems().map((item) => ({
      id: Number(item.variantId),
      quantity: item.quantity,
      properties: { Lagerstatus: this.#lagerstatusFor(item.variantId) },
    }));
  }

  /**
   * Reads the variant id -> stock-status map embedded by the buy-buttons block.
   * @returns {Record<string, string>}
   */
  #getLagerstatusMap() {
    if (this.#lagerstatusMap) return this.#lagerstatusMap;

    const el = this.querySelector('[data-lagerstatus-map]');
    let parsed = {};
    if (el?.textContent) {
      try {
        parsed = JSON.parse(el.textContent);
      } catch (error) {
        parsed = {};
      }
    }
    this.#lagerstatusMap = parsed;
    return parsed;
  }

  /**
   * Warehouse stock-status text for a variant, defaulting to "På lager" for unknown variants
   * (add-to-cart is only enabled for purchasable items).
   * @param {string | number | undefined | null} variantId
   * @returns {string}
   */
  #lagerstatusFor(variantId) {
    if (variantId == null) return 'På lager';
    return this.#getLagerstatusMap()[String(variantId)] || 'På lager';
  }

  /**
   * Keeps the hidden "Lagerstatus" line-item property in sync with the chosen variant so the
   * standard (non-bundle) add-to-cart submits the right status.
   * @param {string | number | undefined | null} variantId
   */
  #updateLagerstatusProperty(variantId) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-lagerstatus-property]'));
    if (input) input.value = this.#lagerstatusFor(variantId);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const bundleItems = this.#getSelectedBundleItems();
    const mainVariantId = formData.get('id')?.toString();
    const mainQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

    if (bundleItems.length > 0 && mainVariantId) {
      this.#processBatchAddToCart(
        [
          {
            variantId: mainVariantId,
            quantity: mainQuantity,
          },
          ...bundleItems,
        ],
        event
      );
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   * @param {Event} [event]
   */
  #processBatchAddToCart(items, event) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => {
        /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
        const entry = {
          id: Number(item.variantId),
          quantity: item.quantity,
        };
        const status = this.#lagerstatusFor(item.variantId);
        if (status) entry.properties = { Lagerstatus: status };
        return entry;
      }),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';
    this.#updateLagerstatusProperty(variantId.value);

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
